import React from 'react';
import { BaseTimelineItemFormLayout } from 'framework/ui/timeline';
import useRiskNotesService from '../../useRiskNotesService';
import { DEFAULT_ERROR_MESSAGE, FormFieldsData, UpdateForm } from 'framework/form';
import { cloneDeep } from 'lodash';
import { ErrorMessage, useMessageService } from 'framework/ui/Message';
import { useProjectId } from 'domain/shared/project';
import { useParams } from 'react-router-dom';
import { NotePayload, RiskNote } from 'domain/shared/newProject';
import RiskNoteForm from '../RiskNoteForm';
import { HTTP } from 'framework/api';

interface RiskNoteTimelineUpdateFormProps {
  messageContext: string;
  entityId?: string;
  afterSubmit?: { execute?: (id: StringOrNumber, data: FormFieldsData) => void };
  getDataFunc: () => Promise<RiskNote>;
  onCancel: () => void;
}

const RiskNoteTimelineUpdateForm = ({
  entityId,
  getDataFunc,
  onCancel,
  messageContext,
  afterSubmit,
  ...rest
}: RiskNoteTimelineUpdateFormProps) => {
  const { updateRiskNote, getNoteValidationSchema } = useRiskNotesService();
  const { addMessage } = useMessageService(messageContext);
  const projectId = useProjectId();
  const { riskId } = useParams();

  const updateFormFunc = async (id: StringOrNumber, values: FormFieldsData): Promise<NotePayload> => {
    const formData = cloneDeep(values) as NotePayload;
    try {
      const data = await updateRiskNote(projectId, riskId!, id.toString(), {
        text: formData.text,
        attachmentPaths: formData.attachmentPaths || [],
      });
      const normalizedData = {
        ...data,
        payload: {
          text: data?.text ?? formData.text,
          attachmentPaths: data?.attachmentPaths ?? formData.attachmentPaths ?? [],
        },
      };
      afterSubmit?.execute?.(id, normalizedData);
      return normalizedData;
    } catch (error: any) {
      if (error?.status === HTTP.FORBIDDEN) {
        addMessage(new ErrorMessage(`You don't have permission to update this note.`));
      } else {
        addMessage(new ErrorMessage(DEFAULT_ERROR_MESSAGE));
      }
      throw error;
    }
  };

  const getMappedDataFunc = async () => {
    const entity = await getDataFunc();
    const note = entity?.payload as { text?: string; attachmentPaths?: string[] } | undefined;
    return {
      text: note?.text ?? '',
      attachmentPaths: note?.attachmentPaths ?? [],
    };
  };

  if (!entityId) {
    return null;
  }

  return (
    <UpdateForm
      {...rest}
      entityId={entityId}
      submitFormFunc={updateFormFunc}
      getDataFunc={getMappedDataFunc}
      getValidationSchemaFunc={getNoteValidationSchema}
    >
      <BaseTimelineItemFormLayout isCreateForm={false} onCancel={onCancel}>
        <RiskNoteForm label={'Note text (not available for customer report)'} />
      </BaseTimelineItemFormLayout>
    </UpdateForm>
  );
};

export default RiskNoteTimelineUpdateForm;
